import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import '../styles/Chat.css';

const API_URL = process.env.REACT_APP_API_URL;

const ICONS = {
  back: <polyline points="15 18 9 12 15 6" />,
  info: (<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4m0-4h.01" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  clip: <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
  send: (<><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>),
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  file: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>),
  close: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  down: (<><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>),
  edit: (<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>),
  trash: (<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>),
};

const Icon = ({ name, className = '' }) => (
  <svg className={`ch-icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

const initialOf = (text) => (text || '?').trim().charAt(0).toUpperCase() || '?';
const toneOf = (text) => ((text || '?').trim().toUpperCase().charCodeAt(0) || 0) % 4;
const getFileExt = (fileName) => (fileName && fileName.includes('.') ? fileName.split('.').pop().slice(0, 4).toUpperCase() : 'DOSYA');
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'])/g;

// Mesaj metnini: bağlantıları tıklanabilir yapar, arama sözcüğünü vurgular
const renderMessageText = (text, query) => {
  if (!text) return null;
  const q = (query || '').trim();
  const highlight = (chunk, keyPrefix) => {
    if (!q) return chunk;
    const parts = chunk.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase()
        ? <mark key={`${keyPrefix}-${i}`} className="ch-hl">{part}</mark>
        : part
    );
  };
  return String(text).split(URL_RE).map((chunk, i) =>
    /^https?:\/\//.test(chunk)
      ? <a key={i} href={chunk} target="_blank" rel="noopener noreferrer" className="ch-link">{chunk}</a>
      : <React.Fragment key={i}>{highlight(chunk, i)}</React.Fragment>
  );
};

/* ---------- Onay penceresi (mobilde alttan açılan panel) ---------- */
const ConfirmDialog = ({ title, message, confirmLabel, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onCancelRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    await onConfirm();
  };

  return (
    <div className="ch-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="ch-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="ch-modal-head">
          <h3>{title}</h3>
          <button type="button" className="ch-icon-btn" onClick={onCancel} disabled={busy} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </div>
        <div className="ch-modal-body">
          <p>{message}</p>
          <div className="ch-modal-actions">
            <button type="button" className="ch-btn ch-btn-outline" onClick={onCancel} disabled={busy}>Vazgeç</button>
            <button type="button" className="ch-btn ch-btn-danger" onClick={handleConfirm} disabled={busy} autoFocus>
              {busy ? 'Siliniyor...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Chat({ groupId = null }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeChat, setActiveChat] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sideFilter, setSideFilter] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedMessageContent, setEditedMessageContent] = useState('');
  const [error, setError] = useState(null);
  const [groupInfoVisible, setGroupInfoVisible] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [groupsStatus, setGroupsStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [socketStatus, setSocketStatus] = useState('connecting'); // 'connecting' | 'connected' | 'disconnected'
  const [socketReady, setSocketReady] = useState(0);
  const [jumpCount, setJumpCount] = useState(0);
  const [unread, setUnread] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [openActionsId, setOpenActionsId] = useState(null);
  // Dokunmatik cihazlarda Enter yeni satır açar, gönderme düğmesiyle gönderilir
  const isTouch = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches, []);

  const messagesBoxRef = useRef(null);
  const activeChatRef = useRef(null);
  const prevLenRef = useRef(0);
  const isSearchingRef = useRef(false);
  const socketRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const dragDepthRef = useRef(0);
  const newMessageRef = useRef('');
  const draftsRef = useRef({});
  const messageRefs = useRef({});
  const navigate = useNavigate();
  activeChatRef.current = activeChat;
  newMessageRef.current = newMessage;

  const fetchGroupMembers = useCallback(async () => {
    if (getChatType() !== 'group' || !activeChat) {
      return;
    }
    setIsLoadingMembers(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/${activeChat}/members`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      setGroupMembers(response.data.map(member => ({
        id: member.id,
        email: member.email,
        firstName: member.first_name || (member.email || '').split('@')[0] || 'Kullanıcı',
        lastName: member.last_name || ''
      })));
    } catch (err) {
      console.error('Hata Detayı (getGroupMembers):', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      });
      setError('Grup üyeleri yüklenemedi: ' + (err.response?.data?.error || err.message));
      setGroupMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const box = messagesBoxRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior });
  }, []);

  const scrollToMessage = useCallback((messageId) => {
    const messageElement = messageRefs.current[messageId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.classList.add('highlighted');
      setTimeout(() => {
        messageElement.classList.remove('highlighted');
      }, 2000);
    }
  }, []);

  const getSenderName = useCallback((msg) => {
    const formatName = (firstName, lastName) => {
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      if (firstName || lastName) {
        return `${firstName || ''} ${lastName || ''}`.trim();
      }
      return null;
    };

    if (msg.senderName) return msg.senderName;
    if (msg.sender_name) return msg.sender_name;

    if (msg.senderId === currentUser?.id) {
      const fullName = formatName(currentUser.firstName, currentUser.lastName);
      return fullName || currentUser.email.split('@')[0];
    }

    const onlineUser = onlineUsers.find(user => user.id === msg.senderId);
    if (onlineUser) {
      const fullName = formatName(onlineUser.firstName, onlineUser.lastName);
      return fullName || onlineUser.email.split('@')[0];
    }

    const groupMember = groupMembers.find(member => member.id === msg.senderId);
    if (groupMember) {
      const fullName = formatName(groupMember.firstName, groupMember.lastName);
      return fullName || groupMember.email.split('@')[0];
    }

    if (msg.senderEmail || msg.sender_email) {
      return (msg.senderEmail || msg.sender_email).split('@')[0];
    }

    return 'Kullanıcı';
  }, [currentUser, onlineUsers, groupMembers]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/zip', 'application/x-rar-compressed',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'video/mp4', 'video/avi', 'audio/mpeg', 'audio/mp3', 'text/plain'
    ];
    if (file.size > maxSize) {
      return { valid: false, message: 'Dosya boyutu 10MB\'dan büyük olamaz.' };
    }
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, message: 'Bu dosya türü desteklenmiyor.' };
    }
    return { valid: true };
  };

  const handleFileClick = async (fileUrl, fileName, storedName) => {
    if (fileUrl) {
      try {
        const token = localStorage.getItem('token');
        const downloadParam = storedName || fileName;

        const response = await axios.get(`${API_URL}/api/download/${downloadParam}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Dosya indirme hatası:', err);
        setError('Dosya indirilemedi: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const getReadStatusIcon = (readStatus) => {
    switch (readStatus) {
      case 'sent': return '✓';
      case 'delivered': return '✓✓';
      case 'read': return <span className="ch-read-done">✓✓</span>;
      default: return '';
    }
  };

  const startEditingMessage = (msg) => {
    if (msg.senderId === currentUser?.id && !(msg.fileUrl || msg.file_url)) {
      setEditingMessageId(msg.id);
      setEditedMessageContent(msg.message);
    }
  };

  const saveEditedMessage = async (messageId) => {
    if (editedMessageContent.trim()) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/api/messages/${messageId}/edit`,
          { messageId, content: editedMessageContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        socketRef.current.emit('editMessage', { messageId, content: editedMessageContent });
        setEditingMessageId(null);
        setEditedMessageContent('');
      } catch (err) {
        console.error('Mesaj düzenleme hatası:', err);
        setError('Mesaj düzenlenemedi: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const deleteMessage = async (messageId) => {
    const messageToDelete = messages.find(msg => msg.id === messageId);

    if (!messageToDelete || messageToDelete.senderId !== currentUser?.id) {
      console.warn('Mesaj silme engellendi: Mesaj bulunamadı veya silme yetkisi yok. ID:', messageId);
      setError('Bu mesajı silme yetkiniz yok.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 204) {
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        socketRef.current.emit('deleteMessage', { messageId });
      } else {
        throw new Error(response.data?.error || 'Silme isteği başarısız oldu.');
      }

    } catch (err) {
      console.error('Mesaj silme hatası:', err.response?.data || err.message);
      setError('Mesaj silinemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const starMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      try {
        await axios.put(
          `${API_URL}/api/messages/${messageId}/pin`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, starred: !msg.starred, pinned: !msg.starred }
              : msg
          )
        );
        socketRef.current?.emit('starMessage', { messageId });
      } catch (pinError) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, starred: !msg.starred }
              : msg
          )
        );
        socketRef.current?.emit('starMessage', { messageId });
      }
    } catch (err) {
      console.error('Mesaj yıldızlama hatası:', err);
      setError('Mesaj yıldızlanamadı: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      if (socketRef.current && activeChat && currentUser) {
        const chatType = getChatType();
        socketRef.current.emit('request_message_history', {
          type: chatType,
          groupId: chatType === 'group' ? activeChat : null,
          otherUserId: chatType === 'private' ? activeChat : null
        });
      }
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          query: searchQuery,
          groupId: getChatType() === 'group' ? activeChat : null,
          userId: getChatType() === 'private' ? activeChat : null
        }
      });
      setMessages(response.data);
    } catch (err) {
      console.error('Mesaj arama hatası:', err);
      setError('Mesaj aranamadı: ' + (err.response?.data?.error || err.message));
    }
  };

  const changeChat = useCallback((chatType, chatId = null) => {
    const id = chatId || chatType;
    // Yazılmakta olan mesaj sohbet değişince kaybolmasın
    draftsRef.current[String(activeChatRef.current)] = newMessageRef.current;
    setNewMessage(draftsRef.current[String(id)] || '');
    setOpenActionsId(null);
    setActiveChat(id);
    setUnread((prev) => {
      if (!prev[String(id)]) return prev;
      const next = { ...prev };
      delete next[String(id)];
      return next;
    });
    isSearchingRef.current = false;
    setSearchQuery('');
    setSearchOpen(false);
    setError(null);
    setEditingMessageId(null);
    setGroupInfoVisible(false);
    setShowMemberList(false);
  }, []);

  const getChatType = () => {
    if (!activeChat) return null;
    if (userGroups.some((group) => group.id === activeChat)) return 'group';
    if (onlineUsers.some((u) => u.id === activeChat)) return 'private';
    return null;
  };

  const canUploadFile = () => {
    const chatType = getChatType();
    return chatType === 'group';
  };

  const getStarredMessages = () => messages.filter((msg) => msg.starred || msg.pinned);

  // Başka bir sayfaya gömüldüğünde (ör. GroupDetail) ilgili grubun sohbetini otomatik aç
  useEffect(() => {
    if (!groupId || activeChat) return;
    const match = userGroups.find((g) => String(g.id) === String(groupId));
    if (match) changeChat('group', match.id);
  }, [groupId, userGroups, activeChat, changeChat]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token bulunamadı. Giriş sayfasına yönlendiriliyor.');
      navigate('/login');
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUser({
        id: payload.userId || payload.id,
        email: payload.email,
        firstName: payload.firstName || payload.email.split('@')[0],
        lastName: payload.lastName || ''
      });
    } catch (e) {
      console.error('Token çözümleme hatası:', e);
      navigate('/login');
    }
  }, [navigate]);

  // Gruplar: önce REST ile yükle (socket gecikse veya düşse bile liste görünsün)
  const loadGroups = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setGroupsStatus('loading');
    try {
      const res = await axios.get(`${API_URL}/api/groups/user`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      const list = Array.isArray(res.data) ? res.data : (res.data?.groups || []);
      // Bu uç nokta tüm grupları döndürebilir: yalnızca üyesi olduğunuz grupları göster
      let myId = null;
      try {
        myId = JSON.parse(atob(token.split('.')[1])).userId ?? null;
      } catch (e) {
        myId = null;
      }
      const mine = list.filter(
        (g) =>
          !Array.isArray(g.members) ||
          myId === null ||
          g.members.some((m) => String(m && typeof m === 'object' ? m.id : m) === String(myId))
      );
      // Socket zaten grup listesi gönderdiyse onu ezme
      setUserGroups((prev) => (prev.length > 0 ? prev : mine));
      setGroupsStatus('ready');
    } catch (err) {
      console.error('Gruplar yüklenemedi:', err.response?.data || err.message);
      setGroupsStatus((prev) => (prev === 'ready' ? 'ready' : 'error'));
    }
  }, []);

  useEffect(() => {
    if (currentUser) loadGroups();
  }, [currentUser, loadGroups]);

  // Socket bağlantısı: sohbet değiştirince yeniden bağlanmaz, yalnızca bir kez kurulur
  useEffect(() => {
    if (!currentUser || socketRef.current) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setSocketStatus('connecting');
    const socket = io(API_URL, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    const mapMessageKeys = (msg) => ({
      ...msg,
      senderId: msg.sender_id || msg.senderId,
      receiverId: msg.receiver_id || msg.receiverId,
      fileUrl: msg.file_url || msg.fileUrl,
      fileName: msg.file_name || msg.fileName,
      storedName: msg.stored_name || msg.storedName,
      createdAt: msg.created_at || msg.createdAt,
      readStatus: msg.read_status || msg.readStatus || 'sent',
      pinned: msg.pinned,
      starred: msg.starred || msg.pinned,
      senderName: msg.sender_name || msg.senderName,
    });

    const bumpUnread = (key) => {
      setUnread((prev) => ({ ...prev, [String(key)]: (prev[String(key)] || 0) + 1 }));
    };

    const handlePrivateMessage = (msg) => {
      const formattedMsg = mapMessageKeys(msg);
      const mine = formattedMsg.senderId === currentUser?.id;
      const current = activeChatRef.current;
      if (!current || (current !== formattedMsg.senderId && current !== formattedMsg.receiverId)) {
        if (!mine) bumpUnread(formattedMsg.senderId);
        return;
      }

      setMessages((prev) => {
        const isDuplicate = prev.some(
          (existingMsg) =>
            existingMsg.id === formattedMsg.id ||
            (existingMsg.senderId === formattedMsg.senderId &&
             existingMsg.receiverId === formattedMsg.receiverId &&
             existingMsg.message === formattedMsg.message &&
             existingMsg.timestamp === formattedMsg.timestamp)
        );
        return isDuplicate ? prev : [...prev, formattedMsg];
      });
      if (!mine) {
        socket.emit('readStatusUpdate', { messageId: formattedMsg.id, status: 'read' });
      }
    };

    const handleGroupMessage = (msg) => {
      const formattedMsg = mapMessageKeys(msg);
      const msgGroupId = formattedMsg.groupId ?? formattedMsg.group_id;
      const current = activeChatRef.current;
      if (!current || String(current) !== String(msgGroupId)) {
        if (formattedMsg.senderId !== currentUser?.id) bumpUnread(msgGroupId);
        return;
      }

      setMessages((prev) =>
        prev.some((existingMsg) => existingMsg.id === formattedMsg.id) ? prev : [...prev, formattedMsg]
      );
      if (formattedMsg.senderId !== currentUser?.id) {
        socket.emit('readStatusUpdate', { messageId: formattedMsg.id, status: 'read' });
      }
    };

    socket.on('connect', () => {
      setSocketStatus('connected');
      setSocketReady((n) => n + 1); // açık sohbete yeniden katıl
      socket.emit('request_user_data');
    });

    socket.on('disconnect', () => {
      setSocketStatus('disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error('Socket.IO Bağlantı Hatası:', err.message);
      setSocketStatus('disconnected');
      setError('Bağlantı hatası: ' + err.message);
    });

    socket.on('groupMessage', handleGroupMessage);
    socket.on('privateMessage', handlePrivateMessage);

    socket.on('online_users', (users) => {
      setOnlineUsers(users.filter((u) => u.id !== currentUser?.id && u.status === 'online'));
    });

    socket.on('user_groups', (groups) => {
      setUserGroups(Array.isArray(groups) ? groups : []);
      setGroupsStatus('ready');
    });

    socket.on('message_history', (history) => {
      setMessages(history.map(mapMessageKeys));
      setHistoryLoading(false);
    });

    socket.on('readStatusUpdate', ({ messageId, status, seenBy }) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, readStatus: status, seenBy: seenBy || msg.seenBy } : msg))
      );
    });

    socket.on('editMessage', (updated) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === updated.id
            ? { ...msg, message: updated.message, edited: true, updatedAt: updated.updatedAt }
            : msg
        )
      );
    });

    socket.on('deleteMessage', ({ messageId }) => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    });

    socket.on('starMessage', (updatedMessage) => {
      const formattedMsg = mapMessageKeys(updatedMessage);
      setMessages((prev) =>
        prev.map((msg) => (msg.id === formattedMsg.id ?
          { ...msg, starred: formattedMsg.starred } : msg))
      );
    });

    socket.on('pinMessage', (updatedMessage) => {
      const formattedMsg = mapMessageKeys(updatedMessage);
      setMessages((prev) =>
        prev.map((msg) => (msg.id === formattedMsg.id ?
          { ...msg, pinned: formattedMsg.pinned } : msg))
      );
    });

    socket.on('error', (data) => {
      console.error('Socket Hatası:', data.message);
      setError('Socket hatası: ' + data.message);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [currentUser]);

  useEffect(() => {
    setMessages([]);
    prevLenRef.current = 0;
    setJumpCount(0);
    setHistoryLoading(false);
    if (socketRef.current && currentUser && activeChat) {
      const chatType = getChatType();
      if (chatType === 'group') {
        setHistoryLoading(true);
        socketRef.current.emit('joinGroup', activeChat);
        socketRef.current.emit('request_message_history', { type: 'group', groupId: activeChat });
      } else if (chatType === 'private') {
        setHistoryLoading(true);
        socketRef.current.emit('joinPrivateChat', { otherUserId: activeChat });
        socketRef.current.emit('request_message_history', { type: 'private', otherUserId: activeChat });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, currentUser, socketReady]);

  // Yeni mesajda yalnızca kullanıcı zaten altındaysa kaydır; değilse "yeni mesaj" düğmesi göster
  useEffect(() => {
    const box = messagesBoxRef.current;
    const prevLen = prevLenRef.current;
    const len = messages.length;
    prevLenRef.current = len;
    if (!box || len === 0) return;

    if (prevLen === 0) {
      scrollToBottom('auto');
      return;
    }
    if (len > prevLen) {
      const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 160;
      const lastIsMine = messages[len - 1]?.senderId === currentUser?.id;
      if (nearBottom || lastIsMine) {
        scrollToBottom();
      } else {
        setJumpCount((n) => n + (len - prevLen));
      }
    }
  }, [messages, scrollToBottom, currentUser]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        isSearchingRef.current = true;
        handleSearch();
      } else if (isSearchingRef.current) {
        isSearchingRef.current = false;
        handleSearch();
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeChat]);

  useEffect(() => {
    if (groupInfoVisible && getChatType() === 'group') {
      fetchGroupMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupInfoVisible, fetchGroupMembers, activeChat]);

  // Geçmiş hiç gelmezse yükleniyor göstergesi takılı kalmasın
  useEffect(() => {
    if (!historyLoading) return undefined;
    const t = setTimeout(() => setHistoryLoading(false), 8000);
    return () => clearTimeout(t);
  }, [historyLoading]);

  // Yazma alanı yüksekliği içeriğe göre büyür (en fazla ~6 satır)
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [newMessage, activeChat]);

  // Sohbet açılınca yazma alanına odaklan
  useEffect(() => {
    if (activeChat && window.matchMedia('(hover: hover)').matches) {
      composerRef.current?.focus();
    }
  }, [activeChat]);

  // Esc: bilgi panelini / aramayı kapat
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (groupInfoVisible) { setGroupInfoVisible(false); setShowMemberList(false); }
      else if (searchOpen && !searchQuery) setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [groupInfoVisible, searchOpen, searchQuery]);

  const pickFile = (file) => {
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.message);
      return false;
    }
    setError(null);
    setSelectedFile(file);
    return true;
  };

  const handleFileChange = (e) => {
    const ok = pickFile(e.target.files[0]);
    if (ok === false) e.target.value = '';
  };

  // Sürükle-bırak ile dosya ekleme
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragEnter = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
  const onDragLeave = (e) => {
    if (!hasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };
  const onDrop = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (!activeChat) return;
    if (!canUploadFile()) {
      setError('Özel mesajlarda dosya paylaşımı desteklenmiyor.');
      return;
    }
    pickFile(e.dataTransfer.files[0]);
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (isSending) return;

    if (selectedFile) {
      const validation = validateFile(selectedFile);
      if (!validation.valid) {
        setError(validation.message);
        return;
      }
      setIsUploading(true);
      setIsSending(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (userGroups.some((group) => group.id === activeChat)) {
        formData.append('groupId', activeChat);
      } else {
        setIsUploading(false);
        setIsSending(false);
        setError('Özel mesajlarda dosya paylaşımı desteklenmiyor.');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Kimlik doğrulama tokenı bulunamadı.');
        const response = await fetch(`${API_URL}/api/upload-file`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Dosya yüklenemedi.');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setTimeout(() => {
          socketRef.current?.emit('request_message_history', { type: 'group', groupId: activeChat });
        }, 500);
      } catch (err) {
        console.error('Dosya yükleme hatası:', err.message);
        setError('Dosya yükleme hatası: ' + err.message);
      } finally {
        setIsUploading(false);
        setIsSending(false);
      }
      return;
    }

    if (newMessage.trim() && activeChat && currentUser && socketRef.current && !isUploading) {
      setIsSending(true);

      const messageData = {
        message: newMessage.trim(),
        readStatus: 'sent',
        senderId: currentUser.id,
        timestamp: new Date().toISOString(),
      };

      // Mesajı hemen temizle ve yazmaya devam edilebilsin
      setNewMessage('');
      composerRef.current?.focus();

      const resetSendingState = () => {
        setIsSending(false);
      };

      // Güvenlik önlemi: callback gelmezse 2 saniye sonra zorla sıfırla
      const timeoutId = setTimeout(resetSendingState, 2000);

      if (userGroups.some((group) => group.id === activeChat)) {
        messageData.groupId = activeChat;
        socketRef.current.emit('groupMessage', messageData, () => {
          clearTimeout(timeoutId);
          resetSendingState();
        });
      } else if (onlineUsers.some((u) => u.id === activeChat)) {
        messageData.receiverId = activeChat;
        socketRef.current.emit('privateMessage', messageData, () => {
          clearTimeout(timeoutId);
          resetSendingState();
        });
      } else {
        // Geçerli chat bulunamadı
        clearTimeout(timeoutId);
        resetSendingState();
      }
    }
  };

  // Enter = gönder, Shift+Enter = yeni satır
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTouch && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!isSending && !isUploading) sendMessage();
    }
  };

  const handleStarredMessageClick = (messageId) => {
    setGroupInfoVisible(false);
    setShowMemberList(false);
    setTimeout(() => {
      scrollToMessage(messageId);
    }, 100);
  };

  const startPrivateChat = (userId) => {
    changeChat('private', userId);
    setGroupInfoVisible(false);
    setShowMemberList(false);
  };

  const formatDateTime = (msg) => {
    const raw = msg.createdAt || msg.created_at || msg.timestamp;
    return raw && !isNaN(new Date(raw))
      ? new Date(raw).toLocaleString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : 'Tarih bilinmiyor';
  };

  const formatTime = (msg) => {
    const raw = msg.createdAt || msg.created_at || msg.timestamp;
    return raw && !isNaN(new Date(raw))
      ? new Date(raw).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
      : '';
  };

  const getMsgDate = (msg) => {
    const raw = msg.createdAt || msg.created_at || msg.timestamp;
    const d = raw ? new Date(raw) : null;
    return d && !isNaN(d) ? d : null;
  };

  const dayLabel = (d) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Bugün';
    if (d.toDateString() === yesterday.toDateString()) return 'Dün';
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const goBack = () => {
    draftsRef.current[String(activeChatRef.current)] = newMessageRef.current;
    setNewMessage('');
    setOpenActionsId(null);
    setActiveChat(null);
  };

  const activeGroup = activeChat ? userGroups.find((g) => g.id === activeChat) : null;
  const activeUser = activeChat ? onlineUsers.find((u) => u.id === activeChat) : null;
  const offlineTarget = !!activeChat && !activeGroup && !activeUser;
  const offlineMember = offlineTarget ? groupMembers.find((m) => m.id === activeChat) : null;
  const activeTitle = activeGroup
    ? activeGroup.name
    : activeUser
      ? (activeUser.firstName || activeUser.email)
      : offlineMember
        ? (offlineMember.firstName || offlineMember.email)
        : 'Kullanıcı';
  const chatAvailable = !!getChatType();
  const canSend = chatAvailable && socketStatus === 'connected';
  const statusLabel = { connected: 'Bağlı', connecting: 'Bağlanıyor…', disconnected: 'Bağlantı yok' }[socketStatus];
  const isSearching = !!searchQuery.trim();

  const sortedMessages = useMemo(
    () => [...messages].sort(
      (a, b) => new Date(a.createdAt || a.created_at || a.timestamp) - new Date(b.createdAt || b.created_at || b.timestamp)
    ),
    [messages]
  );

  const filterText = sideFilter.trim().toLocaleLowerCase('tr-TR');
  const matches = (text) => !filterText || (text || '').toLocaleLowerCase('tr-TR').includes(filterText);
  const visibleGroups = userGroups.filter((g) => matches(g.name));
  const visibleUsers = onlineUsers.filter((u) => matches(`${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`));
  const totalUnread = Object.values(unread).reduce((a, b) => a + b, 0);

  const headSubtitle = activeGroup
    ? (Array.isArray(activeGroup.members) ? `${activeGroup.members.length} üye · grup sohbeti` : 'Grup sohbeti')
    : offlineTarget
      ? 'Çevrimdışı'
      : 'Çevrimiçi';

  return (
    <div className={`ch-page${groupId ? ' is-embedded' : ''}`}>
      <div className={`ch-app${activeChat ? ' has-chat' : ''}${groupId ? ' is-embedded' : ''}`}>
        {/* ================= Sol liste ================= */}
        <aside className="ch-sidebar">
          <div className="ch-side-head">
            <h2>
              Sohbetler
              {totalUnread > 0 && <span className="ch-badge" aria-label={`${totalUnread} okunmamış mesaj`}>{totalUnread > 99 ? '99+' : totalUnread}</span>}
            </h2>
            <span className={`ch-status is-${socketStatus}`} role="status">{statusLabel}</span>
          </div>

          <div className="ch-side-filter">
            <Icon name="search" className="ch-side-filter-icon" />
            <input
              type="text"
              value={sideFilter}
              onChange={(e) => setSideFilter(e.target.value)}
              placeholder="Grup veya kişi bul…"
              aria-label="Sohbetlerde ara"
            />
            {sideFilter && (
              <button type="button" onClick={() => setSideFilter('')} aria-label="Filtreyi temizle">
                <Icon name="close" />
              </button>
            )}
          </div>

          <div className="ch-side-scroll">
            {socketStatus === 'disconnected' && (
              <button type="button" className="ch-retry" onClick={() => socketRef.current?.connect()}>
                Bağlantıyı yeniden dene
              </button>
            )}

            <h3 className="ch-side-label">Gruplarım</h3>
            <ul className="ch-list">
              {visibleGroups.length > 0 ? (
                visibleGroups.map((group) => (
                  <li key={group.id}>
                    <button
                      type="button"
                      className={`ch-item${activeChat === group.id ? ' is-active' : ''}`}
                      onClick={() => changeChat('group', group.id)}
                    >
                      <span className="ch-item-icon"><Icon name="users" /></span>
                      <span className="ch-item-name">{group.name}</span>
                      {unread[String(group.id)] > 0 && <span className="ch-badge">{unread[String(group.id)]}</span>}
                    </button>
                  </li>
                ))
              ) : userGroups.length > 0 ? (
                <li className="ch-side-empty">Eşleşen grup yok.</li>
              ) : groupsStatus === 'loading' ? (
                <li className="ch-side-empty">Gruplar yükleniyor...</li>
              ) : groupsStatus === 'error' ? (
                <li className="ch-side-empty">
                  Gruplar yüklenemedi.{' '}
                  <button type="button" className="ch-text-btn" onClick={loadGroups}>Tekrar dene</button>
                </li>
              ) : (
                <li className="ch-side-empty">
                  Henüz bir gruba üye değilsiniz.{' '}
                  <Link to="/groups" className="ch-text-link">Gruplara göz at</Link>
                </li>
              )}
            </ul>

            <h3 className="ch-side-label">Online kullanıcılar</h3>
            <ul className="ch-list">
              {visibleUsers.length > 0 ? (
                visibleUsers.map((user) => (
                  <li key={user.id}>
                    <button
                      type="button"
                      className={`ch-item${activeChat === user.id ? ' is-active' : ''}`}
                      onClick={() => changeChat('private', user.id)}
                    >
                      <span className="ch-avatar is-online" data-tone={toneOf(user.firstName || user.email)}>
                        {initialOf(user.firstName || user.email)}
                      </span>
                      <span className="ch-item-name">{user.firstName || user.email}</span>
                      {unread[String(user.id)] > 0 && <span className="ch-badge">{unread[String(user.id)]}</span>}
                    </button>
                  </li>
                ))
              ) : (
                <li className="ch-side-empty">{onlineUsers.length > 0 ? 'Eşleşen kişi yok.' : 'Kimse online değil.'}</li>
              )}
            </ul>
          </div>

          <div className="ch-side-foot">
            <Link to="/" className="ch-home-link">← Ana sayfaya dön</Link>
          </div>
        </aside>

        {/* ================= Sohbet alanı ================= */}
        <main
          className="ch-main"
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <header className="ch-head">
            <button type="button" className="ch-back" onClick={goBack} aria-label="Sohbet listesine dön">
              <Icon name="back" />
              {totalUnread > 0 && <span className="ch-dot" />}
            </button>

            {activeChat ? (
              <>
                {activeGroup ? (
                  <span className="ch-head-icon"><Icon name="users" /></span>
                ) : (
                  <span className={`ch-avatar${activeUser ? ' is-online' : ''}`} data-tone={toneOf(activeTitle)}>{initialOf(activeTitle)}</span>
                )}
                <div className="ch-head-text">
                  <h2>{activeTitle}</h2>
                  <p className={activeUser ? 'is-online' : ''}>{headSubtitle}</p>
                </div>
                <button
                  type="button"
                  className={`ch-icon-btn${searchOpen ? ' is-on' : ''}`}
                  onClick={() => {
                    if (searchOpen) setSearchQuery('');
                    setSearchOpen(!searchOpen);
                  }}
                  title="Mesajlarda ara"
                  aria-label="Mesajlarda ara"
                  aria-expanded={searchOpen}
                >
                  <Icon name="search" />
                </button>
                {activeGroup && (
                  <button
                    type="button"
                    className={`ch-icon-btn${groupInfoVisible ? ' is-on' : ''}`}
                    onClick={() => setGroupInfoVisible(!groupInfoVisible)}
                    title="Grup bilgilerini göster"
                    aria-label="Grup bilgilerini göster"
                    aria-expanded={groupInfoVisible}
                  >
                    <Icon name="info" />
                  </button>
                )}
              </>
            ) : (
              <div className="ch-head-text">
                <h2>Sohbet seçin</h2>
              </div>
            )}
          </header>

          {activeChat && searchOpen && (
            <div className="ch-search">
              <Icon name="search" className="ch-search-icon" />
              <input
                type="text"
                placeholder="Bu sohbette mesaj ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ch-search-input"
                aria-label="Mesaj ara"
                autoFocus
              />
              {searchQuery && (
                <button type="button" className="ch-search-clear" onClick={() => setSearchQuery('')} aria-label="Aramayı temizle">
                  <Icon name="close" />
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="ch-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} aria-label="Hata mesajını kapat">
                <Icon name="close" />
              </button>
            </div>
          )}

          <div className="ch-body">
            {/* Grup bilgi paneli */}
            {groupInfoVisible && getChatType() === 'group' && (
              <aside className="ch-info" aria-label="Grup Bilgileri Paneli">
                <div className="ch-info-head">
                  <h3>Grup bilgileri</h3>
                  <button type="button" className="ch-icon-btn" onClick={() => { setGroupInfoVisible(false); setShowMemberList(false); }} aria-label="Paneli kapat">
                    <Icon name="close" />
                  </button>
                </div>

                <section className="ch-info-section">
                  <h4>Yıldızlanan mesajlar</h4>
                  {getStarredMessages().length > 0 ? (
                    <ul className="ch-starred">
                      {getStarredMessages().map((msg) => (
                        <li key={msg.id}>
                          <button type="button" onClick={() => handleStarredMessageClick(msg.id)}>
                            <strong>{getSenderName(msg)}</strong>
                            <span>{msg.message || 'Dosya paylaşımı'}</span>
                            <small>{formatDateTime(msg)}</small>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ch-info-muted">Hiç yıldızlanan mesaj yok.</p>
                  )}
                </section>

                <section className="ch-info-section">
                  <h4>Üye bilgileri</h4>
                  {isLoadingMembers ? (
                    <p className="ch-info-muted">Üyeler yükleniyor...</p>
                  ) : error ? (
                    <p className="ch-info-error">{error}</p>
                  ) : (
                    <>
                      <p className="ch-info-muted">Toplam üye sayısı: <b>{groupMembers.length}</b></p>
                      <button type="button" className="ch-btn ch-btn-outline" onClick={() => setShowMemberList(!showMemberList)}>
                        {showMemberList ? 'Üye listesini kapat' : 'Üyeleri gör'}
                      </button>
                      {showMemberList && groupMembers.length > 0 && (
                        <ul className="ch-members">
                          {groupMembers.map((member) => {
                            const isMe = member.id === currentUser?.id;
                            const online = onlineUsers.some((u) => u.id === member.id);
                            return (
                              <li key={member.id}>
                                <button
                                  type="button"
                                  onClick={() => startPrivateChat(member.id)}
                                  disabled={isMe || !online}
                                  title={isMe ? 'Bu sizsiniz' : online ? 'Birebir sohbet başlat' : 'Kullanıcı çevrimdışı'}
                                >
                                  <span className={`ch-avatar${online ? ' is-online' : ''}`} data-tone={toneOf(member.firstName || member.email)}>
                                    {initialOf(member.firstName || member.email)}
                                  </span>
                                  <span className="ch-member-name">
                                    {member.firstName || member.email} {member.lastName ? `(${member.lastName})` : ''}
                                  </span>
                                  <small>{isMe ? 'Siz' : online ? 'Çevrimiçi' : 'Çevrimdışı'}</small>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                </section>
              </aside>
            )}

            {/* Mesajlar */}
            <div
              className="ch-messages"
              ref={messagesBoxRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) setJumpCount(0);
              }}
            >
              {!activeChat && (
                <div className="ch-empty">
                  <span className="ch-empty-icon"><Icon name="chat" /></span>
                  <h3>Bir sohbet seçin</h3>
                  <p>
                    {userGroups.length === 0 && groupsStatus === 'ready'
                      ? 'Sohbet etmek için önce bir gruba katılın.'
                      : 'Soldaki listeden bir grup ya da online bir kullanıcı seçerek konuşmaya başlayın.'}
                  </p>
                </div>
              )}

              {historyLoading && sortedMessages.length === 0 && (
                <div className="ch-skel" aria-hidden="true">
                  <span /><span className="is-me" /><span /><span className="is-me" />
                </div>
              )}

              {offlineTarget && (
                <p className="ch-notice">Bu kullanıcı şu anda çevrimdışı. Çevrimiçi olduğunda sohbet edebilirsiniz.</p>
              )}

              {activeChat && !offlineTarget && !historyLoading && sortedMessages.length === 0 && (
                <p className="ch-no-messages">
                  {isSearching ? `“${searchQuery.trim()}” için sonuç bulunamadı.` : 'Henüz mesaj yok. İlk mesajı sen yaz.'}
                </p>
              )}

              {isSearching && sortedMessages.length > 0 && (
                <p className="ch-notice">{sortedMessages.length} sonuç gösteriliyor.</p>
              )}

              {sortedMessages.map((msg, index) => {
                const prev = sortedMessages[index - 1];
                const date = getMsgDate(msg);
                const prevDate = prev ? getMsgDate(prev) : null;
                const newDay = !!date && (!prevDate || date.toDateString() !== prevDate.toDateString());
                const mine = msg.senderId === currentUser?.id;
                const sameSender =
                  !!prev && !newDay && !!date && !!prevDate &&
                  prev.senderId === msg.senderId && (date - prevDate) < 5 * 60 * 1000;
                const starred = msg.starred || msg.pinned;
                const isFile = (msg.fileUrl || msg.file_url) && (msg.fileName || msg.file_name);
                const senderName = getSenderName(msg);

                return (
                  <React.Fragment key={msg.id}>
                    {newDay && <div className="ch-day"><span>{dayLabel(date)}</span></div>}

                    <div
                      ref={(el) => (messageRefs.current[msg.id] = el)}
                      className={`ch-msg ${mine ? 'is-me' : ''} ${starred ? 'is-starred' : ''} ${sameSender ? 'is-follow' : ''}`}
                    >
                      {!mine && (
                        sameSender
                          ? <span className="ch-avatar-gap" />
                          : <span className="ch-avatar" data-tone={toneOf(senderName)}>{initialOf(senderName)}</span>
                      )}

                      <div className="ch-msg-col">
                        <div
                          className="ch-bubble"
                          onClick={(e) => {
                            if (isTouch && editingMessageId !== msg.id && !e.target.closest('a, button, textarea')) {
                              setOpenActionsId((id) => (id === msg.id ? null : msg.id));
                            }
                          }}
                        >
                          {!mine && !sameSender && <span className="ch-msg-who">{senderName}</span>}

                          {isFile ? (
                            <button
                              type="button"
                              className="ch-file"
                              onClick={() =>
                                handleFileClick(
                                  msg.fileUrl || msg.file_url,
                                  msg.fileName || msg.file_name,
                                  msg.storedName || msg.stored_name
                                )
                              }
                            >
                              <span className="ch-file-icon">
                                <Icon name="file" />
                                <em>{getFileExt(msg.fileName || msg.file_name)}</em>
                              </span>
                              <span className="ch-file-name">{msg.fileName || msg.file_name}</span>
                              <span className="ch-file-action">İndir</span>
                            </button>
                          ) : editingMessageId === msg.id ? (
                            <div className="ch-edit">
                              <textarea
                                value={editedMessageContent}
                                onChange={(e) => setEditedMessageContent(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey && !isTouch) { e.preventDefault(); saveEditedMessage(msg.id); }
                                  if (e.key === 'Escape') setEditingMessageId(null);
                                }}
                                className="ch-edit-input"
                                rows={Math.min(5, Math.max(1, editedMessageContent.split('\n').length))}
                                autoFocus
                              />
                              <div className="ch-edit-actions">
                                <button type="button" onClick={() => saveEditedMessage(msg.id)}>Kaydet</button>
                                <button type="button" onClick={() => setEditingMessageId(null)}>İptal</button>
                                {!isTouch && <small>Enter: kaydet · Esc: iptal</small>}
                              </div>
                            </div>
                          ) : (
                            <div className="ch-text">{renderMessageText(msg.message, searchQuery)}</div>
                          )}

                          <div className="ch-meta">
                            {msg.edited && <span className="ch-edited">düzenlendi</span>}
                            {starred && <Icon name="star" className="ch-star" />}
                            <time>{formatTime(msg)}</time>
                            {mine && (
                              <span className={`ch-read${msg.readStatus === 'read' ? ' is-read' : ''}`}>
                                {getReadStatusIcon(msg.readStatus)}
                              </span>
                            )}
                          </div>
                        </div>

                        {editingMessageId !== msg.id && (
                          <div className={`ch-actions${openActionsId === msg.id ? ' is-open' : ''}`} role="toolbar" aria-label="Mesaj işlemleri">
                            <button type="button" onClick={() => starMessage(msg.id)} title={starred ? 'Yıldızı kaldır' : 'Yıldızla'}>
                              <Icon name="star" className={starred ? 'is-filled' : ''} />
                              <span>{starred ? 'Kaldır' : 'Yıldızla'}</span>
                            </button>
                            {mine && !isFile && (
                              <button type="button" onClick={() => startEditingMessage(msg)} title="Düzenle">
                                <Icon name="edit" /><span>Düzenle</span>
                              </button>
                            )}
                            {mine && (
                              <button type="button" className="is-danger" onClick={() => { setOpenActionsId(null); setConfirmMsg(msg); }} title="Sil">
                                <Icon name="trash" /><span>Sil</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {jumpCount > 0 && (
              <button
                type="button"
                className="ch-jump"
                onClick={() => { scrollToBottom(); setJumpCount(0); }}
              >
                <Icon name="down" />
                {jumpCount} yeni mesaj
              </button>
            )}

            {isDragging && (
              <div className="ch-drop" aria-hidden="true">
                <Icon name="clip" />
                <strong>{canUploadFile() ? 'Dosyayı buraya bırakın' : 'Bu sohbette dosya paylaşılamaz'}</strong>
                {canUploadFile() && <span>En fazla 10 MB</span>}
              </div>
            )}
          </div>

          {/* Seçili dosya / yükleme durumu */}
          {selectedFile && !isUploading && (
            <div className="ch-attach">
              <span className="ch-attach-ext">{getFileExt(selectedFile.name)}</span>
              <span className="ch-attach-name">
                <strong>{selectedFile.name}</strong> <small>({formatFileSize(selectedFile.size)})</small>
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label="Seçili dosyayı kaldır"
              >
                <Icon name="close" />
              </button>
            </div>
          )}
          {isUploading && (
            <div className="ch-attach is-busy">
              <span className="ch-spinner" />
              <span className="ch-attach-name">Dosya yükleniyor...</span>
            </div>
          )}

          {/* Mesaj yazma alanı */}
          <form
            className="ch-composer"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isSending && !isUploading) sendMessage(e);
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mp3,.txt"
            />
            <button
              type="button"
              className="ch-attach-btn"
              onClick={() => fileInputRef.current.click()}
              disabled={!activeChat || !canUploadFile() || isUploading}
              title={canUploadFile() ? 'Dosya yükle' : 'Bu sohbet tipinde dosya paylaşımı desteklenmiyor'}
              aria-label="Dosya ekle"
            >
              <Icon name="clip" />
            </button>
            <textarea
              ref={composerRef}
              className="ch-input"
              rows={1}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder={
                !activeChat ? 'Önce bir sohbet seçin' :
                offlineTarget ? 'Kullanıcı çevrimdışı' :
                socketStatus !== 'connected' ? 'Bağlantı bekleniyor...' :
                isUploading ? 'Dosya yükleniyor...' :
                selectedFile ? `Dosya seçildi: ${selectedFile.name}` :
                (isTouch ? 'Mesaj yaz…' : 'Mesaj yaz… (Enter: gönder, Shift+Enter: yeni satır)')
              }
              disabled={!activeChat || !canSend || !!selectedFile || isUploading}
              aria-label="Mesaj yaz"
            />
            <button
              type="submit"
              className="ch-send"
              disabled={!activeChat || !canSend || (!newMessage.trim() && !selectedFile) || isUploading || isSending}
              aria-label={selectedFile ? 'Dosyayı yükle' : 'Gönder'}
            >
              <span>{isUploading ? 'Yükleniyor...' : selectedFile ? 'Dosya yükle' : 'Gönder'}</span>
              <Icon name="send" />
            </button>
          </form>
        </main>
      </div>

      {confirmMsg && (
        <ConfirmDialog
          title="Mesajı sil"
          message="Bu mesaj herkes için silinecek. Bu işlem geri alınamaz."
          confirmLabel="Sil"
          onCancel={() => setConfirmMsg(null)}
          onConfirm={async () => {
            try {
              await deleteMessage(confirmMsg.id);
            } finally {
              setConfirmMsg(null);
            }
          }}
        />
      )}
    </div>
  );
}

export default Chat;